import posthog from "posthog-js"
import { config } from "/app.config"

export const POSTHOG_KEY = process.env.VUE_APP_POSTHOG_PROJECT_TOKEN
export const POSTHOG_HOST = process.env.VUE_APP_POSTHOG_HOST || "https://us.i.posthog.com"

/** Parent flag — control | all-trials. Must match PostHog exactly. */
export const PRICING_EXPERIMENT_FLAG = "pricing-trial-experiment-v2"
/** Nested B vs C flag — only read when parent is all-trials. */
export const PRICING_NUDGE_FLAG = "pricing-annual-nudge-exp"
/** Event property `experiment` value (not the flag key). */
export const PRICING_EXPERIMENT_NAME = "pricing-trial-v2"

const VARIANT_STORAGE_KEY = "ph_pricing_experiment_variant"
const NUDGE_STORAGE_KEY = "ph_pricing_nudge_variant"
/** Sticky QA arm from ?arm= / ?forcePlan=1 — survives Keycloak/login redirects if query is lost. */
const QA_ARM_STORAGE_KEY = "ph_pricing_experiment_qa"
const QA_NUDGE_STORAGE_KEY = "ph_pricing_nudge_qa"
/** One-shot gate so getFeatureFlag only emits $feature_flag_called once per browser tab session. */
const EXPOSURE_SENT_KEY = "ph_pricing_exposure_sent"

const PARENT_ARMS = new Set(["control", "all-trials"])
const NUDGE_ARMS = new Set(["no-nudge", "nudge"])

/** Cancel-flow retention offer order flag — control (50% off annual first) | pause-first. Must match PostHog exactly. */
export const RETENTION_EXPERIMENT_FLAG = "cancel-retention-offer-exp"
/** Event property `experiment` value (not the flag key). */
export const RETENTION_EXPERIMENT_NAME = "cancel-retention-offer-order"
export const RETENTION_ARM_ANNUAL_FIRST = "control"
export const RETENTION_ARM_PAUSE_FIRST = "pause-first"
const RETENTION_ARMS = new Set([RETENTION_ARM_ANNUAL_FIRST, RETENTION_ARM_PAUSE_FIRST])

/** Offer-description copy test — control (current bullets) | test (Radio Automation on Prime and Premier). */
export const OFFER_COPY_EXPERIMENT_FLAG = "offer-description-copy-test"
const OFFER_COPY_ARMS = new Set(["control", "test"])

/** Experiment metrics only — anything else is dropped at capture. */
const ALLOWED_EVENTS = new Set([
  "plan_cta_clicked",
  "paid_plan_created",
  "plan_page_viewed",
  "annual_nudge_shown",
  "annual_nudge_accepted",
  "annual_nudge_dismissed",
  "cancel_flow_started",
  "cancel_offer_shown",
  "cancel_offer_declined",
  "cancel_offer_accepted",
  "cancel_offer_dismissed",
  "cancel_portal_opened",
  "plan_finder_opened",
  "plan_finder_completed",
  "plan_finder_continue_clicked",
])

const BOOTSTRAP_COOKIE = "ph_bootstrap"

export const isPostHogEnabled = () => {
  return Boolean(POSTHOG_KEY && POSTHOG_KEY.length && config.isZeno)
}

/**
 * Map QA query tokens → parent + nudge assignment.
 * @returns {{ parent: 'control'|'all-trials', nudge: 'no-nudge'|'nudge'|null }|null}
 */
const mapQaToken = (arm, forcePlan) => {
  if (arm === "control") {
    return { parent: "control", nudge: null }
  }
  if (arm === "all-trials" || arm === "no-nudge") {
    return { parent: "all-trials", nudge: "no-nudge" }
  }
  if (arm === "nudge") {
    return { parent: "all-trials", nudge: "nudge" }
  }
  // Deprecated v1 aliases → all-trials + no-nudge for one release
  if (arm === "test" || forcePlan === "1") {
    return { parent: "all-trials", nudge: "no-nudge" }
  }
  return null
}

const persistQaAssignment = (assignment) => {
  try {
    sessionStorage.setItem(QA_ARM_STORAGE_KEY, assignment.parent)
    sessionStorage.setItem(VARIANT_STORAGE_KEY, assignment.parent)
    if (assignment.nudge) {
      sessionStorage.setItem(QA_NUDGE_STORAGE_KEY, assignment.nudge)
      sessionStorage.setItem(NUDGE_STORAGE_KEY, assignment.nudge)
    } else {
      sessionStorage.removeItem(QA_NUDGE_STORAGE_KEY)
      sessionStorage.removeItem(NUDGE_STORAGE_KEY)
    }
  } catch (e) {
    // ignore
  }
}

/**
 * Review overrides from URL (preferred) or sessionStorage.
 * `?arm=control|all-trials|no-nudge|nudge` (aliases: test, forcePlan=1 → all-trials+no-nudge).
 * `?arm=clear` removes the sticky override.
 * @param {Record<string, string>|undefined} routeQuery optional vue-router query
 * @returns {'control'|'all-trials'|null} parent arm only (use getQueryNudgeOverride for B/C)
 */
export const getQueryArmOverride = (routeQuery) => {
  const assignment = getQueryAssignmentOverride(routeQuery)
  return assignment ? assignment.parent : null
}

/**
 * @param {Record<string, string>|undefined} routeQuery
 * @returns {'no-nudge'|'nudge'|null}
 */
export const getQueryNudgeOverride = (routeQuery) => {
  const assignment = getQueryAssignmentOverride(routeQuery)
  if (!assignment || assignment.parent !== "all-trials") {
    return null
  }
  return assignment.nudge
}

/**
 * Full QA assignment (parent + nudge).
 * @param {Record<string, string>|undefined} routeQuery
 * @returns {{ parent: 'control'|'all-trials', nudge: 'no-nudge'|'nudge'|null }|null}
 */
export const getQueryAssignmentOverride = (routeQuery) => {
  let arm = null
  let forcePlan = null

  try {
    const params = new URLSearchParams(window.location.search)
    arm = params.get("arm")
    forcePlan = params.get("forcePlan")
  } catch (e) {
    // URL parsing failed
  }

  if (routeQuery) {
    if (!arm && routeQuery.arm) {
      arm = Array.isArray(routeQuery.arm) ? routeQuery.arm[0] : routeQuery.arm
    }
    if (!forcePlan && routeQuery.forcePlan != null) {
      forcePlan = Array.isArray(routeQuery.forcePlan) ? routeQuery.forcePlan[0] : String(routeQuery.forcePlan)
    }
  }

  if (arm === "clear") {
    try {
      sessionStorage.removeItem(QA_ARM_STORAGE_KEY)
      sessionStorage.removeItem(QA_NUDGE_STORAGE_KEY)
      sessionStorage.removeItem(VARIANT_STORAGE_KEY)
      sessionStorage.removeItem(NUDGE_STORAGE_KEY)
    } catch (e) {
      // ignore
    }
    return null
  }

  const fromQuery = mapQaToken(arm, forcePlan)
  if (fromQuery) {
    persistQaAssignment(fromQuery)
    return fromQuery
  }

  try {
    const stickyParent = sessionStorage.getItem(QA_ARM_STORAGE_KEY)
    if (PARENT_ARMS.has(stickyParent)) {
      const stickyNudge = sessionStorage.getItem(QA_NUDGE_STORAGE_KEY)
      return {
        parent: stickyParent,
        nudge: stickyParent === "all-trials" && NUDGE_ARMS.has(stickyNudge) ? stickyNudge : stickyParent === "all-trials" ? "no-nudge" : null,
      }
    }
  } catch (e) {
    // sessionStorage unavailable
  }

  return null
}

const clearBootstrapCookie = () => {
  document.cookie = `${BOOTSTRAP_COOKIE}=; path=/; max-age=0`
  document.cookie = `${BOOTSTRAP_COOKIE}=; domain=.zeno.fm; path=/; max-age=0`
}

const parseBootstrapPayload = (raw) => {
  if (!raw) {
    return null
  }

  try {
    let decoded = raw
    try {
      decoded = decodeURIComponent(raw)
    } catch (e) {
      // already decoded
    }

    const data = JSON.parse(decoded)
    const distinctID = data.distinctID || data.distinct_id
    const sessionID = data.sessionID || data.session_id

    if (!distinctID) {
      return null
    }

    return {
      distinctID,
      ...(sessionID ? { sessionID } : {}),
    }
  } catch (e) {
    return null
  }
}

const readBootstrapFromCookie = () => {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${BOOTSTRAP_COOKIE}=([^;]*)`))
    if (!match) {
      return null
    }

    const bootstrap = parseBootstrapPayload(match[1])
    clearBootstrapCookie()
    return bootstrap
  } catch (e) {
    return null
  }
}

const readBootstrapFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search)
    const distinctID = params.get("ph_distinct_id")
    const sessionID = params.get("ph_session_id")

    if (!distinctID) {
      return null
    }

    return {
      distinctID,
      ...(sessionID ? { sessionID } : {}),
    }
  } catch (e) {
    return null
  }
}

const persistPricingAssignment = (parent, nudge) => {
  if (getQueryAssignmentOverride()) {
    return
  }

  try {
    if (PARENT_ARMS.has(parent)) {
      sessionStorage.setItem(VARIANT_STORAGE_KEY, parent)
    } else {
      sessionStorage.removeItem(VARIANT_STORAGE_KEY)
    }

    if (parent === "all-trials" && NUDGE_ARMS.has(nudge)) {
      sessionStorage.setItem(NUDGE_STORAGE_KEY, nudge)
    } else {
      sessionStorage.removeItem(NUDGE_STORAGE_KEY)
    }
  } catch (e) {
    // sessionStorage unavailable
  }
}

const hasSentPricingExposure = () => {
  try {
    return sessionStorage.getItem(EXPOSURE_SENT_KEY) === "1"
  } catch (e) {
    return false
  }
}

const markPricingExposureSent = () => {
  try {
    sessionStorage.setItem(EXPOSURE_SENT_KEY, "1")
  } catch (e) {
    // sessionStorage unavailable
  }
}

const clearPricingExposureSent = () => {
  try {
    sessionStorage.removeItem(EXPOSURE_SENT_KEY)
  } catch (e) {
    // sessionStorage unavailable
  }
}

const isInternalTestEmail = (email) => {
  if (!email || typeof email !== "string") {
    return false
  }
  return email.toLowerCase().endsWith("@zenomedia.com")
}

/**
 * Resolve nested flags in order: parent, then nudge only if all-trials.
 * First successful resolve per tab session emits `$feature_flag_called` (PostHog experiment exposure).
 * Later reads stay silent to avoid spam. Skip exposure under QA `?arm=` overrides.
 * @param {{ recordExposure?: boolean }} [options]
 * @returns {{ parent: 'control'|'all-trials'|null, nudge: 'no-nudge'|'nudge'|null }}
 */
const resolveLiveAssignment = ({ recordExposure = false } = {}) => {
  const underQa = Boolean(getQueryAssignmentOverride())
  const sendEvent = Boolean(recordExposure && !underQa && !hasSentPricingExposure())

  const parent = posthog.getFeatureFlag(PRICING_EXPERIMENT_FLAG, { send_event: sendEvent })
  if (!PARENT_ARMS.has(parent)) {
    return { parent: null, nudge: null }
  }

  if (parent !== "all-trials") {
    if (sendEvent) {
      markPricingExposureSent()
    }
    return { parent, nudge: null }
  }

  const nudge = posthog.getFeatureFlag(PRICING_NUDGE_FLAG, { send_event: sendEvent })
  if (sendEvent) {
    markPricingExposureSent()
  }
  return {
    parent,
    nudge: NUDGE_ARMS.has(nudge) ? nudge : null,
  }
}

/**
 * Init PostHog once (Zeno brand only). Prefer URL bootstrap IDs from the marketing
 * site, then the cross-subdomain cookie handoff, so pricing-experiment persons stay linked.
 */
export const initPostHog = () => {
  if (!isPostHogEnabled()) {
    return null
  }

  if (window.__posthogInitialized) {
    return posthog
  }

  const bootstrapIds = readBootstrapFromUrl() || readBootstrapFromCookie() || {}
  const bootstrap = {}

  if (bootstrapIds.distinctID) {
    bootstrap.distinctID = bootstrapIds.distinctID
  }
  if (bootstrapIds.sessionID) {
    bootstrap.sessionID = bootstrapIds.sessionID
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    ui_host: "https://us.posthog.com",
    persistence: "localStorage+cookie",
    capture_pageview: false,
    capture_pageleave: false,
    autocapture: false,
    disable_session_recording: true,
    ...(Object.keys(bootstrap).length ? { bootstrap } : {}),
  })

  // Cache nested assignment before Keycloak identify() can change flag hashing.
  // First resolve emits `$feature_flag_called` once per tab (experiment exposure).
  posthog.onFeatureFlags(() => {
    const { parent, nudge } = resolveLiveAssignment({ recordExposure: true })
    persistPricingAssignment(parent, nudge)
  })

  window.__posthogInitialized = true
  return posthog
}

export const identifyPostHogUser = (user) => {
  if (!isPostHogEnabled() || !user?.id) {
    return
  }

  try {
    const props = {
      email: user.email,
      first_name: user.basic_info?.first_name,
      last_name: user.basic_info?.last_name,
    }
    // Project "filter test accounts" cohort matches email @zenomedia.com OR this flag.
    if (isInternalTestEmail(user.email)) {
      props.$internal_or_test_user = true
    }
    posthog.identify(String(user.id), props)
  } catch (e) {
    console.log("[PostHog] identify error: ", e)
  }
}

export const resetPostHogUser = () => {
  if (!isPostHogEnabled()) {
    return
  }

  try {
    // Drops the distinct_id and cached flag assignments. Without it the next
    // person to sign in on this browser is stitched onto the previous user's
    // PostHog person and inherits their experiment arm.
    posthog.reset()
    clearPricingExposureSent()
  } catch (e) {
    console.log("[PostHog] reset error: ", e)
  }
}

export const trackPostHogEvent = (eventName, properties = {}) => {
  try {
    if (!isPostHogEnabled()) {
      return
    }

    if (!ALLOWED_EVENTS.has(eventName)) {
      return
    }

    posthog.capture(eventName, properties)
  } catch (e) {
    console.log("[PostHog] Error: ", e)
  }
}

const PAID_PLAN_SESSION_STORAGE_PREFIX = "ph_paid_plan_created_"

/**
 * Claim a checkout session so conversion tracking runs at most once per browser tab session.
 * @param {string|null|undefined} sessionId Stripe Checkout session id (cs_…)
 * @returns {boolean} true if this call should fire conversion events
 */
export const claimPaidPlanCreatedSession = (sessionId) => {
  if (!sessionId || typeof sessionId !== "string") {
    return true
  }

  const key = PAID_PLAN_SESSION_STORAGE_PREFIX + sessionId
  try {
    if (sessionStorage.getItem(key)) {
      return false
    }
    sessionStorage.setItem(key, "1")
    return true
  } catch (e) {
    return true
  }
}

/**
 * Fire paid_plan_created once per Stripe checkout session (survives remount / refresh).
 * @param {string|null|undefined} sessionId
 * @param {Record<string, unknown>} properties
 * @returns {boolean} whether the event was sent
 */
export const trackPaidPlanCreatedOnce = (sessionId, properties = {}) => {
  if (!claimPaidPlanCreatedSession(sessionId)) {
    return false
  }

  trackPostHogEvent("paid_plan_created", {
    ...properties,
    ...(sessionId ? { checkout_session_id: sessionId } : {}),
  })
  return true
}

/**
 * Parent pricing experiment variant for the current person.
 * @returns {'control'|'all-trials'|null}
 */
export const getPricingExperimentVariant = (routeQuery) => {
  const qa = getQueryAssignmentOverride(routeQuery)
  if (qa) {
    return qa.parent
  }

  if (!isPostHogEnabled()) {
    return null
  }

  try {
    if (posthog.featureFlags && posthog.featureFlags.hasLoadedFlags) {
      const { parent, nudge } = resolveLiveAssignment({ recordExposure: true })
      persistPricingAssignment(parent, nudge)
      return parent
    }

    try {
      const sticky = sessionStorage.getItem(VARIANT_STORAGE_KEY)
      if (PARENT_ARMS.has(sticky)) {
        return sticky
      }
    } catch (e) {
      // sessionStorage unavailable
    }
  } catch (e) {
    console.log("[PostHog] getFeatureFlag error: ", e)
  }

  return null
}

/**
 * Nested nudge arm — only meaningful when parent is all-trials.
 * @returns {'no-nudge'|'nudge'|null}
 */
export const getAnnualNudgeVariant = (routeQuery) => {
  const qa = getQueryAssignmentOverride(routeQuery)
  if (qa) {
    return qa.parent === "all-trials" ? qa.nudge : null
  }

  if (!isPostHogEnabled()) {
    return null
  }

  try {
    if (posthog.featureFlags && posthog.featureFlags.hasLoadedFlags) {
      const { parent, nudge } = resolveLiveAssignment({ recordExposure: true })
      persistPricingAssignment(parent, nudge)
      return parent === "all-trials" ? nudge : null
    }

    try {
      const stickyParent = sessionStorage.getItem(VARIANT_STORAGE_KEY)
      if (stickyParent !== "all-trials") {
        return null
      }
      const stickyNudge = sessionStorage.getItem(NUDGE_STORAGE_KEY)
      return NUDGE_ARMS.has(stickyNudge) ? stickyNudge : null
    } catch (e) {
      // sessionStorage unavailable
    }
  } catch (e) {
    console.log("[PostHog] getAnnualNudgeVariant error: ", e)
  }

  return null
}

export const isAnnualNudgeEnabled = (routeQuery) => {
  return getAnnualNudgeVariant(routeQuery) === "nudge"
}

/**
 * Shared props so plan CTA + paid conversion join cleanly in PostHog.
 * Dual-writes `variant` (matches marketing plan CTA taxonomy) + `experiment_variant`.
 * For all-trials users, always includes explicit `nudge_variant` for B vs C insights.
 * @param {'control'|'all-trials'|null|undefined} armOverride optional parent arm already applied to the plan UI
 * @returns {Record<string, string|null>}
 */
export const getPricingExperimentEventProps = (armOverride, routeQuery) => {
  const qa = getQueryAssignmentOverride(routeQuery)
  const arm =
    armOverride === "control" || armOverride === "all-trials"
      ? armOverride
      : getPricingExperimentVariant(routeQuery)

  const props = {
    experiment: PRICING_EXPERIMENT_NAME,
    experiment_variant: arm,
    variant: arm || "baseline",
  }

  if (arm === "all-trials") {
    props.nudge_variant = getAnnualNudgeVariant(routeQuery)
  } else {
    props.nudge_variant = null
  }

  // Sticky ?arm= / ?forcePlan overrides — filter these out of experiment/HogQL readouts.
  if (qa) {
    props.qa_override = true
  }

  return props
}

/**
 * QA override for the retention experiment: `?cancelOffer=control|pause-first` on the page the cancel button is on.
 * @returns {'control'|'pause-first'|null}
 */
const getRetentionQaArm = () => {
  try {
    const arm = new URLSearchParams(window.location.search).get("cancelOffer")
    return RETENTION_ARMS.has(arm) ? arm : null
  } catch (e) {
    return null
  }
}

/**
 * Arm of the cancel-flow retention offer experiment. Reading the flag records the exposure
 * (`$feature_flag_called`), so call it only once the person has asked to cancel and is eligible for every arm.
 * @returns {'control'|'pause-first'|null} null = not enrolled (PostHog off, flags not loaded, or the flag off)
 */
export const getRetentionExperimentVariant = () => {
  const qa = getRetentionQaArm()
  if (qa) {
    return qa
  }

  if (!isPostHogEnabled()) {
    return null
  }

  try {
    if (!posthog.featureFlags || !posthog.featureFlags.hasLoadedFlags) {
      return null
    }

    const arm = posthog.getFeatureFlag(RETENTION_EXPERIMENT_FLAG)
    return RETENTION_ARMS.has(arm) ? arm : null
  } catch (e) {
    console.log("[PostHog] getRetentionExperimentVariant error: ", e)
    return null
  }
}

/**
 * Shared props for the cancel-flow events. `experiment_variant` is null for people outside the experiment.
 * @param {'control'|'pause-first'|null} arm
 * @returns {Record<string, string|boolean|null>}
 */
export const getRetentionExperimentEventProps = (arm) => {
  const props = {
    experiment: RETENTION_EXPERIMENT_NAME,
    experiment_variant: arm,
  }

  if (getRetentionQaArm()) {
    props.qa_override = true
  }

  return props
}

/**
 * PostHog distinct id for Stripe metadata handoff (webhook → paid_plan_created).
 * @returns {string|null}
 */
export const getPostHogDistinctId = () => {
  if (!isPostHogEnabled()) {
    return null
  }

  try {
    return posthog.get_distinct_id() || null
  } catch (e) {
    return null
  }
}

/**
 * QA override for the offer-copy experiment: `?offerCopy=control|test`.
 * @returns {'control'|'test'|null}
 */
const getOfferCopyQaArm = () => {
  try {
    const arm = new URLSearchParams(window.location.search).get("offerCopy")
    return OFFER_COPY_ARMS.has(arm) ? arm : null
  } catch (e) {
    return null
  }
}

/**
 * Arm of the offer-description copy test. Reading the flag records exposure, so call it
 * when the plan highlights are on screen.
 * @returns {'control'|'test'|null} null = not enrolled (PostHog off, flags not loaded, or the flag off)
 */
export const getOfferDescriptionCopyVariant = () => {
  const qa = getOfferCopyQaArm()
  if (qa) {
    return qa
  }

  if (!isPostHogEnabled()) {
    return null
  }

  try {
    if (!posthog.featureFlags || !posthog.featureFlags.hasLoadedFlags) {
      return null
    }

    const arm = posthog.getFeatureFlag(OFFER_COPY_EXPERIMENT_FLAG)
    return OFFER_COPY_ARMS.has(arm) ? arm : null
  } catch (e) {
    console.log("[PostHog] getOfferDescriptionCopyVariant error: ", e)
    return null
  }
}

/**
 * Call `callback(variant)` now and whenever flags reload.
 * With `?offerCopy=`, fires once and does not read the flag.
 * @param {(variant: 'control'|'test'|null) => void} callback
 * @returns {Function} unsubscribe
 */
export const onOfferCopyExperimentReady = (callback) => {
  const qa = getOfferCopyQaArm()
  if (qa) {
    callback(qa)
    return () => {}
  }

  if (!isPostHogEnabled()) {
    callback(null)
    return () => {}
  }

  const apply = () => callback(getOfferDescriptionCopyVariant())

  if (posthog.featureFlags && posthog.featureFlags.hasLoadedFlags) {
    apply()
  }

  return posthog.onFeatureFlags(apply)
}

/**
 * Call `callback({ experimentVariant, nudgeVariant })` now and whenever flags reload.
 * With QA override: fires once and never listens to PostHog flags.
 * @returns {Function} unsubscribe
 */
export const onPricingExperimentReady = (callback, routeQuery) => {
  const qa = getQueryAssignmentOverride(routeQuery)
  if (qa) {
    callback({
      experimentVariant: qa.parent,
      nudgeVariant: qa.parent === "all-trials" ? qa.nudge : null,
    })
    return () => {}
  }

  if (!isPostHogEnabled()) {
    callback({ experimentVariant: null, nudgeVariant: null })
    return () => {}
  }

  const apply = () => {
    callback({
      experimentVariant: getPricingExperimentVariant(routeQuery),
      nudgeVariant: getAnnualNudgeVariant(routeQuery),
    })
  }

  if (posthog.featureFlags && typeof posthog.featureFlags.hasLoadedFlags === "boolean") {
    if (posthog.featureFlags.hasLoadedFlags) {
      apply()
    }
  }

  return posthog.onFeatureFlags(apply)
}
